import React from "react";

/**
 * Icon — the canonical Armstrong icon primitive.
 *
 * Wraps any Phosphor / Lucide icon component and enforces the platform's
 * three discipline rules so contributors can't drift the visual language:
 *
 *   1. Weight is locked to "duotone" by default. Contentful icons stay
 *      duotone (depth + warmth); the only allowed exceptions are
 *      `weight="bold"` (action chevrons / leading affordances) and
 *      `weight="fill"` (status dots, checkmarks, stars, ornament glyphs).
 *
 *   2. Tone is one of a small enum that maps to the named Tailwind
 *      tokens we ship:
 *        ink      → text-ink           neutral default, body / structural
 *        gold     → text-gold          premium / verified / featured
 *        crimson  → text-crimson       destructive / urgent / female
 *        muted    → text-[#6B6B6B]     low-priority metadata
 *        white    → text-white         on dark surfaces
 *        success  → text-[#0F8043]     traffic-light OK (semantic only)
 *        warning  → text-[#8A6A00]     traffic-light warn (semantic only)
 *        current  → (inherits)         when parent already sets a colour
 *      You CANNOT pass an arbitrary hex; off-palette colours are how the
 *      platform drifted into a SaaS rainbow before.
 *
 *   3. Size is one of the canonical scale tokens, which maps to:
 *        xs   → 14px   inline with text, badges
 *        sm   → 16px   primary nav, button leading icon
 *        md   → 18px   list rows, card avatars
 *        lg   → 22px   section headers, stat tiles
 *        xl   → 28px   featured cards, the WaxSeal medallion
 *        hero → 40px   empty states, hero icons
 *      A raw number is still allowed (escape hatch for tight pill /
 *      badge alignment that needs e.g. 11/13) but emits a dev warning
 *      so reviewers see it during code review.
 *
 * Usage:
 *   <Icon as={Dog} tone="gold" size="lg" />
 *   <Icon as={ArrowRight} tone="muted" size="xs" weight="bold" />
 *   <Icon as={CheckCircle} tone="success" size="sm" weight="fill" />
 */

const TONE_CLASS = {
  ink:     "text-ink",
  gold:    "text-gold",
  crimson: "text-crimson",
  muted:   "text-[#6B6B6B]",
  white:   "text-white",
  success: "text-[#0F8043]",
  warning: "text-[#8A6A00]",
  current: "",
};

const SIZE_TOKENS = {
  xs:   14,
  sm:   16,
  md:   18,
  lg:   22,
  xl:   28,
  hero: 40,
};

const ALLOWED_WEIGHTS = new Set(["duotone", "bold", "fill", "regular"]);

// Dev-only invariant — fires once per offending call site per session
// so the console doesn't flood. Keys on a synthesized identity tuple.
const _warned = typeof window !== "undefined" ? new Set() : null;
function devWarn(key, message) {
  if (process.env.NODE_ENV === "production") return;
  if (_warned && _warned.has(key)) return;
  _warned && _warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[Icon] ${message}`);
}

export const Icon = React.forwardRef(function Icon(
  {
    as: Component,
    tone = "ink",
    size = "sm",
    weight = "duotone",
    className = "",
    ...rest
  },
  ref,
) {
  if (!Component) {
    devWarn("missing-as", "Icon expects an `as` prop with a Phosphor / Lucide component.");
    return null;
  }

  // Tone validation
  let toneClass;
  if (Object.prototype.hasOwnProperty.call(TONE_CLASS, tone)) {
    toneClass = TONE_CLASS[tone];
  } else {
    devWarn(
      `tone:${tone}`,
      `Unknown tone "${tone}". Allowed: ${Object.keys(TONE_CLASS).join(", ")}. Falling back to "ink".`,
    );
    toneClass = TONE_CLASS.ink;
  }

  // Size validation — token preferred, raw number allowed but warned
  let pixelSize;
  if (typeof size === "string" && Object.prototype.hasOwnProperty.call(SIZE_TOKENS, size)) {
    pixelSize = SIZE_TOKENS[size];
  } else if (typeof size === "number") {
    pixelSize = size;
    const canonical = Object.values(SIZE_TOKENS);
    if (!canonical.includes(size) && ![12, 20, 24, 32, 48, 64].includes(size)) {
      devWarn(
        `size:${size}`,
        `Non-canonical icon size ${size}. Prefer a token (${Object.keys(SIZE_TOKENS).join(", ")}) or one of [12, 20, 24, 32, 48, 64].`,
      );
    }
  } else {
    devWarn(`size:${size}`, `Unknown size "${size}". Allowed: ${Object.keys(SIZE_TOKENS).join(", ")} or a number.`);
    pixelSize = SIZE_TOKENS.sm;
  }

  // Weight validation
  if (!ALLOWED_WEIGHTS.has(weight)) {
    devWarn(
      `weight:${weight}`,
      `Unknown weight "${weight}". Allowed: ${Array.from(ALLOWED_WEIGHTS).join(", ")}. Falling back to "duotone".`,
    );
    weight = "duotone";
  }

  // Phosphor accepts `size` as a number or string. Lucide accepts the
  // same. The shared className composition keeps tailwind purge happy.
  return (
    <Component
      ref={ref}
      size={pixelSize}
      weight={weight}
      className={`${toneClass} ${className}`.trim()}
      {...rest}
    />
  );
});

export default Icon;

/**
 * Public size-token export for sites that need to know the pixel value
 * (e.g. svg `viewBox` math, animation deltas, container sizing).
 */
export const ICON_SIZES = { ...SIZE_TOKENS };
